export const PASSWORD_MIN_LENGTH = 8;
export * from "./payslip.js";
export const ORDER_PAYMENT_CHANNELS = ["阿里便捷", "阿里自营", "创一公户工行8249", "私户民生", "万可瑞兹农行", "香港账户", "创一公户农行8877", "创一公户中行4910"];
export const ORDER_EXPORT_METHODS = ["创一正式报关", "万可正式报关", "非正式报关"];
export const CUSTOMS_DOCUMENT_TYPES = ["合同及形式发票", "报关单", "装箱单", "放行条", "船东提单", "退税凭证", "国内运费发票及水单", "国际运费发票及水单", "委托协议", "报关发票", "外汇收汇结汇回单", "销售备案", "进项税发票", "其他"];
/** Convert a material's CNY base price into the selected quotation currency. */
export function convertCnyPriceToCurrency(priceCny, exchangeRateToCny) {
    assertMoneyConversionInput(priceCny, exchangeRateToCny);
    return roundCurrencyAmount(priceCny / exchangeRateToCny);
}
/** Preserve an edited quotation price as its CNY equivalent for later currency switches. */
export function convertCurrencyPriceToCny(price, exchangeRateToCny) {
    assertMoneyConversionInput(price, exchangeRateToCny);
    return roundCurrencyAmount(price * exchangeRateToCny);
}
/** Apply an internal PI markup (positive) or discount (negative) to a currency amount. */
export function applyQuotationPriceAdjustment(basePrice, adjustmentPercent) {
    if (!Number.isFinite(basePrice) || basePrice < 0)
        throw new RangeError("Base price must be a non-negative finite number");
    if (!Number.isFinite(adjustmentPercent) || adjustmentPercent < -100 || adjustmentPercent > 1_000) {
        throw new RangeError("Quotation price adjustment must be between -100% and 1000%");
    }
    return roundCurrencyAmount(basePrice * (1 + adjustmentPercent / 100));
}
/** Calculate the customer-visible amount for one PI line from its displayed unit price. */
export function calculateQuotationLineAmount(quantity, unitPrice) {
    if (!Number.isFinite(quantity) || quantity < 0)
        throw new RangeError("Quantity must be a non-negative finite number");
    if (!Number.isFinite(unitPrice) || unitPrice < 0)
        throw new RangeError("Unit price must be a non-negative finite number");
    return roundCurrencyAmount(quantity * unitPrice);
}
/** Adjust a PI line quantity by one whole unit while allowing the line to reach zero. */
export function nudgeQuotationLineQuantity(quantity, direction) {
    if (!Number.isFinite(quantity) || quantity < 0)
        throw new RangeError("Quantity must be a non-negative finite number");
    return Math.max(0, Math.min(1_000_000, Math.round((quantity + direction) * 1_000) / 1_000));
}
/** Sum rounded PI line amounts so editor, saved PI, preview, and PDF agree to the cent. */
export function calculateQuotationTotal(lines) {
    return roundCurrencyAmount(lines.reduce((sum, line) => sum + calculateQuotationLineAmount(line.quantity, line.unitPrice), 0));
}
/**
 * Keep the customer-agreed product total independent from its detail prices, then
 * add freight exactly once to obtain the final PI total.
 * When no product total is supplied, the rounded detail subtotal remains authoritative.
 */
export function calculateQuotationPricing(lines, finalTotalAmount, freightAmount = 0) {
    const lineSubtotal = calculateQuotationTotal(lines);
    if (!Number.isFinite(freightAmount) || freightAmount < 0) {
        throw new RangeError("Quotation freight must be a non-negative finite number");
    }
    const roundedFreightAmount = roundCurrencyAmount(freightAmount);
    if (finalTotalAmount !== undefined && finalTotalAmount !== null && (!Number.isFinite(finalTotalAmount) || finalTotalAmount < 0)) {
        throw new RangeError("Final quotation product total must be a non-negative finite number");
    }
    const productSubtotal = finalTotalAmount === undefined || finalTotalAmount === null
        ? lineSubtotal
        : roundCurrencyAmount(finalTotalAmount);
    return {
        lineSubtotal,
        overallAdjustmentAmount: roundCurrencyAmount(productSubtotal - lineSubtotal),
        productSubtotal,
        freightAmount: roundedFreightAmount,
        totalAmount: roundCurrencyAmount(productSubtotal + roundedFreightAmount)
    };
}
export function resolveQuotationFreightAmount(quotation) {
    const explicit = Number(quotation.freightAmount);
    if (Number.isFinite(explicit) && explicit > 0)
        return roundCurrencyAmount(explicit);
    const productSubtotal = Number(quotation.productSubtotal);
    const totalAmount = Number(quotation.totalAmount);
    if (!Number.isFinite(productSubtotal) || !Number.isFinite(totalAmount))
        return 0;
    return roundCurrencyAmount(Math.max(0, totalAmount - productSubtotal));
}
/** Calculate the two customer-visible payment rows used by a commercial invoice. */
export function calculateCommercialInvoicePayments(totalAmount, advancePaymentPercent, currentPaymentPercent) {
    if (!Number.isFinite(totalAmount) || totalAmount < 0)
        throw new RangeError("Commercial invoice total must be non-negative");
    for (const percent of [advancePaymentPercent, currentPaymentPercent]) {
        if (!Number.isFinite(percent) || percent < 0 || percent > 100)
            throw new RangeError("Commercial invoice payment percentages must be between 0 and 100");
    }
    if (advancePaymentPercent + currentPaymentPercent > 100)
        throw new RangeError("Commercial invoice payment percentages cannot exceed 100 in total");
    return {
        advancePaymentAmount: roundCurrencyAmount(totalAmount * advancePaymentPercent / 100),
        currentPaymentAmount: roundCurrencyAmount(totalAmount * currentPaymentPercent / 100)
    };
}
/** Convert each CNY cost to the PI currency before line rounding, matching customer price totals. */
export function calculateQuotationCostTotal(lines, exchangeRateToCny) {
    return calculateQuotationTotal(lines.map((line) => ({
        quantity: line.quantity,
        unitPrice: convertCnyPriceToCurrency(line.costPriceCny, exchangeRateToCny)
    })));
}
/** Use the PI reference sale price when configured; otherwise quote from the material cost. */
export function selectQuotationBasePriceCny(referenceSalePriceCny, costPriceCny) {
    if (!Number.isFinite(referenceSalePriceCny) || referenceSalePriceCny < 0) {
        throw new RangeError("Reference sale price must be a non-negative finite number");
    }
    if (!Number.isFinite(costPriceCny) || costPriceCny < 0) {
        throw new RangeError("Cost price must be a non-negative finite number");
    }
    return referenceSalePriceCny > 0 ? referenceSalePriceCny : costPriceCny;
}
export function roundCurrencyAmount(value) {
    return Math.round((value + Number.EPSILON) * 100) / 100;
}
function assertMoneyConversionInput(value, exchangeRateToCny) {
    if (!Number.isFinite(value) || value < 0)
        throw new RangeError("Price must be a non-negative finite number");
    if (!Number.isFinite(exchangeRateToCny) || exchangeRateToCny <= 0)
        throw new RangeError("Exchange rate must be a positive finite number");
}
const CATALOG_UNIT_ENGLISH = new Map([
    ["个", "pc"], ["件", "pc"], ["只", "pc"], ["块", "pc"], ["把", "pc"],
    ["台", "unit"], ["套", "set"], ["包", "pack"], ["卷", "roll"], ["对", "pair"], ["副", "pair"],
    ["张", "sheet"], ["本", "copy"], ["条", "pc"], ["米", "m"], ["平方", "m²"],
    ["公斤", "kg"], ["人", "person"], ["次", "time"], ["通道", "channel"],
    ["项", "item"], ["元", "CNY"]
]);
/** Localize a stored catalog unit for display without changing the canonical master-data value. */
export function localizeCatalogUnit(value, language) {
    if (language !== "en")
        return value;
    const trimmed = value.trim();
    return CATALOG_UNIT_ENGLISH.get(trimmed) ?? trimmed;
}
export function formatCatalogSpecifications(specifications, language) {
    const seen = new Set();
    const values = [];
    for (const specification of specifications) {
        const name = (specification?.name ?? "").trim();
        const unit = (specification?.unit ?? "").trim();
        let value = (specification?.value ?? "").trim();
        for (;;) {
            const lowered = value.toLocaleLowerCase();
            const fullWidth = `${name.toLocaleLowerCase()}：`;
            const halfWidth = `${name.toLocaleLowerCase()}:`;
            if (lowered.startsWith(fullWidth))
                value = value.slice(fullWidth.length).trim();
            else if (lowered.startsWith(halfWidth))
                value = value.slice(halfWidth.length).trim();
            else
                break;
        }
        const key = `${name.toLocaleLowerCase()}\u0000${value.toLocaleLowerCase()}\u0000${unit.toLocaleLowerCase()}`;
        if (seen.has(key))
            continue;
        seen.add(key);
        const localizedName = localizeCatalogSpecificationPart(name, language, "name");
        const localizedValue = localizeCatalogSpecificationPart(value, language, "value");
        const localizedUnit = localizeCatalogSpecificationPart(unit, language, "unit");
        values.push(`${localizedName}${language === "en" ? ": " : "："}${localizedValue}${localizedUnit ? ` ${localizedUnit}` : ""}`);
    }
    return values.join(language === "en" ? "; " : "；") || (language === "en" ? "No specifications" : "未填写规格");
}
export function localizeLegacyCatalogSpecification(value, language) {
    if (language !== "en" || !value.trim())
        return value;
    return value.split(/[；;]/).map((part) => {
        const trimmed = part.trim();
        const separator = trimmed.includes("：") ? "：" : trimmed.includes(":") ? ":" : "";
        if (!separator)
            return localizeCatalogSpecificationPart(trimmed, language, "value");
        const [rawName, ...rest] = trimmed.split(separator);
        return `${localizeCatalogSpecificationPart(rawName.trim(), language, "name")}: ${localizeCatalogSpecificationPart(rest.join(separator).trim(), language, "value")}`;
    }).join("; ");
}
function localizeCatalogSpecificationPart(value, language, kind) {
    if (language !== "en" || !value)
        return value;
    if (kind === "unit")
        return localizeCatalogUnit(value, language);
    const dictionaries = {
        name: new Map([["规格", "Specification"], ["型号", "Model"], ["尺寸", "Size"], ["人数", "Capacity"], ["颜色", "Color"], ["材质", "Material"], ["长度", "Length"], ["宽度", "Width"], ["高度", "Height"], ["重量", "Weight"], ["分辨率", "Resolution"]]),
        value: new Map([["整套", "Complete set"], ["单人", "1 person"], ["一人", "1 person"], ["默认", "Default"], ["常规", "Standard"], ["标准", "Standard"], ["标配", "Standard"], ["公", "Male"], ["母", "Female"], ["绿色", "Green"], ["红色", "Red"], ["黄色", "Yellow"], ["粉色", "Pink"], ["灰色", "Gray"], ["黑色", "Black"], ["深黑色", "Deep black"], ["蓝精灵-256", "Blue Elf-256"]])
    };
    const direct = dictionaries[kind].get(value);
    if (direct)
        return direct;
    const peoplePerUnit = /^(\d+)人\/个$/.exec(value);
    if (peoplePerUnit)
        return `${peoplePerUnit[1]} people / pc`;
    const cards = /^一人配(\d+)-(\d+)张$/.exec(value);
    if (cards)
        return `1 person / ${cards[1]}-${cards[2]} cards`;
    return value
        .replace(/蓝精灵/g, "Blue Elf")
        .replace(/超五类/g, "Cat5e ")
        .replace(/六类/g, "Cat6 ")
        .replace(/富德/g, "Fude ")
        .replace(/欧标/g, "EU Standard")
        .replace(/国标/g, "CN Standard")
        .replace(/美标/g, "US Standard")
        .replace(/英标/g, "UK Standard")
        .replace(/红外/g, "Infrared ")
        .replace(/电容/g, "Capacitive ")
        .replace(/高配/g, "High Configuration")
        .replace(/标配/g, "Standard")
        .replace(/常规/g, "Standard")
        .replace(/整套/g, "Complete set")
        .replace(/千兆交换机/g, "Gigabit switch")
        .replace(/无源/g, "Passive")
        .replace(/弯/g, "Angled")
        .replace(/直/g, "Straight")
        .replace(/公对公/g, "Male to Male")
        .replace(/公对母/g, "Male to Female")
        .replace(/母对母/g, "Female to Female")
        .replace(/四代/g, " 4th Gen ")
        .replace(/(\d+(?:\.\d+)?(?:-\d+(?:\.\d+)?)?)寸/g, "$1 in")
        .replace(/(\d+(?:\.\d+)?)米/g, "$1 m")
        .replace(/(\d+)人/g, "$1 people")
        .replace(/(\d+)口/g, "$1-port")
        .replace(/(\d+)路/g, "$1-channel")
        .replace(/(\d+)块/g, "$1 pcs")
        .replace(/(\d+)个/g, "$1 pcs")
        .replace(/深黑色/g, "Deep black")
        .replace(/灰色/g, "Gray")
        .replace(/绿色|绿/g, "Green")
        .replace(/红色|红/g, "Red")
        .replace(/黄色|黄/g, "Yellow")
        .replace(/粉色|粉/g, "Pink")
        .replace(/\s+/g, " ")
        .trim();
}
/** Group PI detail rows by complete product while keeping standalone items together. */
export function quotationProductGroups(lines) {
    const groups = new Map();
    for (const line of lines) {
        const key = line.productTemplateId ?? "__standalone__";
        const existing = groups.get(key);
        if (existing) {
            existing.lines.push(line);
            existing.totalAmount = roundCurrencyAmount(existing.totalAmount + line.lineTotal);
            continue;
        }
        groups.set(key, {
            key,
            productTemplateId: line.productTemplateId,
            productName: line.productTemplateId
                ? line.productNameEn || line.productName || "Unnamed product"
                : "Standalone Hardware / Software",
            productSku: line.productTemplateId ? line.productSku : "ITEMS",
            lines: [line],
            totalAmount: roundCurrencyAmount(line.lineTotal)
        });
    }
    return [...groups.values()];
}
/** One customer-facing row per complete product; standalone items remain top-level products. */
export function quotationWholeProductRows(lines) {
    const rows = [];
    const seenProducts = new Set();
    for (const line of lines) {
        if (line.productTemplateId) {
            if (seenProducts.has(line.productTemplateId))
                continue;
            seenProducts.add(line.productTemplateId);
            rows.push({ key: line.productTemplateId, name: line.productNameEn || line.productName || line.itemNameEn || line.itemName });
        }
        else {
            rows.push({ key: line.id, name: line.itemNameEn || line.itemName });
        }
    }
    return rows;
}
/**
 * Returns only the customer-facing delivery details entered on the quotation.
 * Customer-master address data is intentionally excluded because it may contain
 * internal sales notes that must never appear in a PI preview or exported PDF.
 */
export function quotationPrintCustomerDetails(quotation) {
    return {
        contact: [quotation.consigneeName, quotation.consigneePhone]
            .map((value) => value.trim())
            .filter(Boolean)
            .join(" | "),
        address: quotation.shippingAddress.trim()
    };
}
export const FINANCE_ACCOUNT_CURRENCIES = ["CNY", "USD", "HKD", "EUR", "JPY"];
export * from "./after-sales.js";
